const ACTIVITY_LABELS = {
  USER_REGISTER: 'User registered',
  USER_UPDATE: 'User updated',
  USER_DELETE: 'User deleted',
  PRODUCT_CREATE: 'Product created',
  PRODUCT_UPDATE: 'Product updated',
  PRODUCT_DELETE: 'Product deleted',
  STORE_PRICE_UPDATE: 'Store price updated',
  STORE_PRICE_DELETE: 'Store price deleted',
  SALE_PROCESS: 'Sale processed',
  INVENTORY_ADJUST: 'Inventory adjusted',
  REFUND_PROCESS: 'Refund processed',
  CUSTOMER_CREATE: 'Customer created',
  CUSTOMER_UPDATE: 'Customer updated',
  CUSTOMER_DELETE: 'Customer deleted',
  PROMOTION_CREATE: 'Promotion created',
  PROMOTION_UPDATE: 'Promotion updated',
  PROMOTION_DELETE: 'Promotion deleted',
  STORE_CREATE: 'Store created',
  STORE_UPDATE: 'Store updated',
  STORE_DELETE: 'Store deactivated',
  STORE_SETTINGS_CREATE: 'Store setting created',
  STORE_SETTINGS_UPDATE: 'Store setting updated',
  STORE_SETTINGS_DELETE: 'Store setting deleted',
  ROLE_CREATE: 'Role created',
  ROLE_UPDATE: 'Role updated',
  ROLE_DELETE: 'Role deleted',
  SETTINGS_UPDATE: 'Settings updated',
  TRANSFER_CREATE: 'Transfer created',
  TRANSFER_UPDATE: 'Transfer updated',
  RETURN_TO_SUPPLIER: 'Return to supplier recorded',
  RETURN_TO_SUPPLIER_CREATE: 'Return to supplier created',
  PURCHASE_ORDER_STATUS_UPDATE: 'Purchase order updated',
  INVOICE_UPDATE: 'Invoice updated',
  INVOICE_STATUS_UPDATE: 'Invoice status updated',
  B2B_ORDER_CREATE: 'B2B order created',
  B2B_ORDER_UPDATE: 'B2B order updated',
  B2B_ORDER_STATUS_UPDATE: 'B2B order status updated',
  B2B_INVOICE_CREATE: 'B2B invoice created',
  B2B_INVOICE_UPDATE: 'B2B invoice updated',
  B2B_INVOICE_PAYMENT_RECORD: 'B2B invoice payment recorded',
  B2B_INVOICE_PDF_GENERATE: 'B2B invoice PDF generated',
  PRODUCT_BULK_UPDATE_UPLOAD: 'Bulk update uploaded',
  PRODUCT_BULK_UPDATE_VALIDATE: 'Bulk update validated',
  PRODUCT_BULK_UPDATE_APPLY: 'Bulk update applied',
  PRODUCT_BULK_UPDATE_ROLLBACK: 'Bulk update rolled back',
};

const DISPLAY_NAME_KEYS = [
  'name',
  'label',
  'title',
  'email',
  'userId',
  'sku',
  'barcode',
  'receiptNumber',
  'invoiceNumber',
  'poNumber',
  'transferId',
  'tillName',
  'sessionLabel',
];

const FIELD_LABELS = {
  receiptNumber: 'Receipt Number',
  subtotal: 'Subtotal',
  totalDiscount: 'Total Discount',
  gstAmount: 'GST Amount',
  total: 'Total',
  paymentMethod: 'Payment Method',
  paymentSource: 'Payment Source',
  rounding: 'Rounding',
  finalAmount: 'Final Amount',
  cashTendered: 'Cash Tendered',
  cashChange: 'Cash Change',
  user: 'User',
  store: 'Store',
  till: 'Till',
  tillSession: 'Till Session',
  appliedPromotions: 'Applied Promotions',
  paymentAllocations: 'Payment Allocations',
  invoiceNumber: 'Invoice Number',
  poNumber: 'Purchase Order Number',
  transferId: 'Transfer Number',
};

const OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i;
const MASKED_VALUE = '[Hidden]';
const HIDE_RAW_REFERENCE_FIELDS = new Set([
  'paymentAllocations',
  'appliedPromotions',
  'roles',
  'stores',
  'permissions',
]);
const SENSITIVE_FIELD_PATTERNS = [
  /password/i,
  /^pin$/i,
  /secret/i,
  /token/i,
  /integration.?key/i,
  /api.?key/i,
  /publishable.?api.?key/i,
  /secret.?api.?key/i,
  /merchant.?key/i,
  /access.?key/i,
  /private.?key/i,
  /credential/i,
];

const isPlainObject = (value) => (
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  !(value instanceof Date)
);

const formatFieldLabel = (field) => (
  FIELD_LABELS[field] || String(field || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const isSensitiveField = (field) => {
  const normalized = String(field || '').trim();
  if (!normalized) return false;
  return SENSITIVE_FIELD_PATTERNS.some((pattern) => pattern.test(normalized));
};

const isObjectIdString = (value) => (
  typeof value === 'string' && OBJECT_ID_REGEX.test(value.trim())
);

const isObjectIdArray = (value) => (
  Array.isArray(value) && value.length > 0 && value.every((item) => isObjectIdString(item))
);

const hasReadableObjectContent = (value) => {
  if (!isPlainObject(value)) return false;
  return Boolean(
    getObjectLabel(value) &&
    !isObjectIdString(getObjectLabel(value))
  );
};

const isReferenceOnlyValue = (value) => (
  isObjectIdString(value) ||
  isObjectIdArray(value) ||
  (Array.isArray(value) && value.length > 0 && value.every((item) => isPlainObject(item) && !hasReadableObjectContent(item)))
);

const getObjectLabel = (value) => {
  if (!isPlainObject(value)) return null;

  const firstName = typeof value.firstName === 'string' ? value.firstName.trim() : '';
  const lastName = typeof value.lastName === 'string' ? value.lastName.trim() : '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  if (fullName) return fullName;

  for (const key of DISPLAY_NAME_KEYS) {
    if (typeof value[key] === 'string' && value[key].trim()) {
      return value[key].trim();
    }
  }

  if (value._id) return String(value._id);
  if (value.id) return String(value.id);

  const entries = Object.entries(value)
    .filter(([, entryValue]) => entryValue !== undefined && entryValue !== null && entryValue !== '')
    .slice(0, 3)
    .map(([key, entryValue]) => `${formatFieldLabel(key)}: ${formatActivityValue(entryValue, key)}`);

  return entries.length ? entries.join(', ') : null;
};

export const formatActivityValue = (value, fieldName = '') => {
  if (isSensitiveField(fieldName)) {
    return MASKED_VALUE;
  }

  if (value === null || value === undefined || value === '') {
    return 'None';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    return value.map((item) => formatActivityValue(item, fieldName)).join(', ');
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'None';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (isPlainObject(value)) {
    return getObjectLabel(value) || 'None';
  }

  return String(value);
};

const getRecordName = (record, fallback) => {
  if (!record) return fallback;
  return getObjectLabel(record) || fallback;
};

const getManualDiscountSnapshotEntries = (items) => (
  (Array.isArray(items) ? items : [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.manualDiscountReason || Number(item?.manualDiscount || 0) > 0)
    .flatMap(({ item, index }) => {
      const productLabel = item.name || `Item ${index + 1}`;
      const manualDiscount = Number(item.manualDiscount || 0);
      const entries = [{
        key: `manualDiscount-${index}`,
        label: `Discount — ${productLabel}`,
        value: manualDiscount > 0 ? `$${manualDiscount.toFixed(2)}` : 'Applied',
      }];
      if (item.manualDiscountReason?.name) {
        entries.push({
          key: `manualDiscountReason-${index}`,
          label: `Discount Reason — ${productLabel}`,
          value: item.manualDiscountReason.name,
        });
      }
      return entries;
    })
);

export const getActivityPrimaryIdentifier = (log) => {
  const snapshots = [log?.after, log?.before];
  const preferredKeys = ['receiptNumber', 'invoiceNumber', 'poNumber', 'transferId', 'name', 'title', 'label'];

  for (const snapshot of snapshots) {
    if (!isPlainObject(snapshot)) continue;
    for (const key of preferredKeys) {
      if (typeof snapshot[key] === 'string' && snapshot[key].trim()) {
        return snapshot[key].trim();
      }
      if (typeof snapshot[key] === 'number' && Number.isFinite(snapshot[key])) {
        return String(snapshot[key]);
      }
    }
  }

  return '';
};

export const getActivityDetailTitle = (log) => {
  const entity = log?.entity || 'Record';
  const identifier = getActivityPrimaryIdentifier(log);
  return identifier ? `Details for ${entity} ${identifier}` : `Details for ${entity}`;
};

export const getActivityActionLabel = (action) => (
  ACTIVITY_LABELS[action] || formatFieldLabel(String(action || '').toLowerCase())
);

export const getActivitySummary = (log) => {
  const afterName = getRecordName(log?.after, String(log?.entityId || 'record'));
  const beforeName = getRecordName(log?.before, String(log?.entityId || 'record'));

  switch (log?.action) {
    case 'PRODUCT_CREATE':
      return `Created product: ${afterName}`;
    case 'PRODUCT_UPDATE':
      return `Updated product: ${afterName}`;
    case 'PRODUCT_DELETE':
      return `Deleted product: ${beforeName}`;
    case 'STORE_PRICE_UPDATE':
      return `Updated store price for ${afterName}`;
    case 'STORE_PRICE_DELETE':
      return `Deleted store price for ${beforeName}`;
    case 'SALE_PROCESS':
      return `Sale ${log?.after?.receiptNumber || log?.entityId} for $${Number(log?.after?.finalAmount || 0).toFixed(2)}`;
    case 'USER_REGISTER':
      return `Registered user: ${afterName}`;
    case 'USER_UPDATE':
      return `Updated user: ${afterName}`;
    case 'USER_DEACTIVATE':
      return `Deactivated user: ${afterName}`;
    case 'USER_REACTIVATE':
      return `Reactivated user: ${afterName}`;
    case 'USER_DELETE':
      return `Deleted user: ${beforeName}`;
    case 'CUSTOMER_CREATE':
      return `Created customer: ${afterName}`;
    case 'CUSTOMER_UPDATE':
      return `Updated customer: ${afterName}`;
    case 'CUSTOMER_DELETE':
      return `Deleted customer: ${beforeName}`;
    case 'PROMOTION_CREATE':
      return `Created promotion: ${afterName}`;
    case 'PROMOTION_UPDATE':
      return `Updated promotion: ${afterName}`;
    case 'PROMOTION_DELETE':
      return `Deleted promotion: ${beforeName}`;
    case 'REFUND_PROCESS':
      return `Refund for sale ${log?.after?.receiptNumber || log?.entityId} of $${Number(log?.after?.refundAmount || 0).toFixed(2)}`;
    case 'STORE_CREATE':
      return `Created store: ${afterName}`;
    case 'STORE_UPDATE':
      return `Updated store: ${afterName}`;
    case 'STORE_DELETE':
      return `Deactivated store: ${beforeName}`;
    case 'STORE_SETTINGS_CREATE':
      return `Created ${log?.entity || 'store setting'}: ${afterName}`;
    case 'STORE_SETTINGS_UPDATE':
      return `Updated ${log?.entity || 'store setting'}: ${afterName}`;
    case 'STORE_SETTINGS_DELETE':
      return `Deleted ${log?.entity || 'store setting'}: ${beforeName}`;
    case 'TRANSFER_CREATE':
      return `Created transfer ${log?.after?.transferId || log?.entityId}`;
    case 'TRANSFER_UPDATE':
      return `Updated transfer ${log?.after?.transferId || log?.entityId}`;
    case 'PURCHASE_ORDER_STATUS_UPDATE':
      return `Purchase order ${log?.after?.poNumber || log?.entityId}: ${formatActivityValue(log?.before?.status)} to ${formatActivityValue(log?.after?.status)}`;
    case 'INVOICE_STATUS_UPDATE':
      return `Invoice ${log?.after?.invoiceNumber || log?.entityId}: ${formatActivityValue(log?.before?.status)} to ${formatActivityValue(log?.after?.status)}`;
    case 'INVOICE_UPDATE':
      return `Updated invoice ${log?.after?.invoiceNumber || log?.entityId}`;
    case 'PRODUCT_BULK_UPDATE_UPLOAD':
    case 'PRODUCT_BULK_UPDATE_VALIDATE':
    case 'PRODUCT_BULK_UPDATE_APPLY':
    case 'PRODUCT_BULK_UPDATE_ROLLBACK':
      return `${getActivityActionLabel(log?.action)}: ${afterName}`;
    default:
      return `${getActivityActionLabel(log?.action)} for ${log?.entity || 'record'} ${log?.entityId || ''}`.trim();
  }
};

export const getActivityChangedFields = (before = {}, after = {}) => {
  const allKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const ignoredKeys = ['_id', '__v', 'updatedAt', 'createdAt', 'items'];

  return Array.from(allKeys)
    .filter((key) => !ignoredKeys.includes(key))
    .filter((key) => JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]))
    .map((key) => ({
      key,
      label: formatFieldLabel(key),
      before: formatActivityValue(before?.[key], key),
      after: formatActivityValue(after?.[key], key),
    }));
};

export const getActivitySnapshotEntries = (snapshot = {}) => {
  const entries = Object.entries(snapshot || {})
    .filter(([key, value]) => !['_id', '__v', 'updatedAt', 'createdAt', 'items'].includes(key) && value !== undefined)
    .filter(([key, value]) => !(HIDE_RAW_REFERENCE_FIELDS.has(key) && isReferenceOnlyValue(value)))
    .map(([key, value]) => ({
      key,
      label: formatFieldLabel(key),
      value: formatActivityValue(value, key),
    }));

  return [...entries, ...getManualDiscountSnapshotEntries(snapshot.items)];
};
